import { Breadcrumb } from "antd";
import { useRouter } from "next/router";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { Toaster } from "react-hot-toast";
import { Body } from "sunday-component";
import Image from "next/image";
import { stringify } from "qs";
import { User } from "src/types/user";
import { filterParams } from "src/utils/url";
import { useTranslation } from "react-i18next";
import UserProfileMenu from "../User/UserMenu";
import {
  Container,
  CustomContent,
  CustomLayout,
  CustomSider,
  PrevButton,
  StyledBreadcrumb,
  StyledLink,
} from "./style/UserProfileLayout.style";

type BreadcrumbNameMap = {
  [key: string]: string;
};

type CustomBreadcrumbProp = {
  breadcrumbName: BreadcrumbNameMap;
};

/**
 * Renders a custom breadcrumb component based on the current URL path and the provided breadcrumbNameMap.
 */
const CustomBreadcrumb = ({ breadcrumbName }: CustomBreadcrumbProp) => {
  const { asPath, query } = useRouter();
  const filteredParams = filterParams(query);
  const { md, xs } = useBreakpoint();
  const pathSnippets = asPath
    .split("/")
    .map((p) => p.split("?")[0]) // remove query string
    .filter((path) => path !== "" && path !== "user"); // remove empty string and "user"

  const extraBreadcrumbItems = pathSnippets.map((path, index) => {
    if (!path) return null;
    const url = `/${pathSnippets.slice(0, index + 1).join("/")}`;
    const queryParams =
      Object.keys(filteredParams).length > 0
        ? `?${stringify(filteredParams)}`
        : "";
    return (
      <Breadcrumb.Item separator=">" key={url}>
        <Body level={7}>
          <a href={`/user${url}${queryParams}`}>{breadcrumbName[url]}</a>
        </Body>
      </Breadcrumb.Item>
    );
  });

  if ((md && extraBreadcrumbItems.length > 2) || xs) {
    const url = `/${pathSnippets.slice(0, -1).join("/")}`;
    return (
      <PrevButton>
        <StyledLink href={`/user${url}`}>
          <Image src="/static/arrow-left-icon.svg" width={16} height={16} />
          <Body level={7}>{breadcrumbName[url]}</Body>
        </StyledLink>
      </PrevButton>
    );
  }

  return (
    <StyledBreadcrumb separator=">">{extraBreadcrumbItems}</StyledBreadcrumb>
  );
};

export type WithLayoutPropType = {
  children: JSX.Element;
  hideSidebar?: boolean;
  breadcrumb?: boolean;
  contentPadding?: string;
  user: User;
};

export const withUserProfileLayout = (WrappedComponent) => {
  return (props: WithLayoutPropType) => {
    const { breadcrumb, hideSidebar, contentPadding, user } = props;
    const { t } = useTranslation("user-profile");
    const router = useRouter();
    const { locale } = router;
    const { lg } = useBreakpoint();
    const shouldHideSidebar = hideSidebar || !user;

    const breadcrumbNameMap = {
      "/setting": t("setting.title"),
      "/setting/personal-info": t("editPersonalInfo.title"),
    };

    // Dev-note: to prevent /default/404, /default/500 pre-render error on build time
    if (locale === "default") {
      return (
        <CustomContent>
          <WrappedComponent {...props} />
        </CustomContent>
      );
    }

    return (
      <Container>
        <Toaster />
        <CustomLayout hideSidebar={shouldHideSidebar}>
          {lg && !shouldHideSidebar ? (
            <CustomSider>
              <UserProfileMenu user={user} />
            </CustomSider>
          ) : null}
          {breadcrumb && (
            <CustomBreadcrumb breadcrumbName={breadcrumbNameMap} />
          )}
          <CustomContent padding={contentPadding}>
            <WrappedComponent {...props} />
          </CustomContent>
        </CustomLayout>
      </Container>
    );
  };
};
